import React from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import * as Yup from 'yup';
import { FormikErrors, FormikTouched } from 'formik';
import { Step as StepProps } from '@service-victoria/component-library/components/Wizard/props';
import * as Interfaces from 'interfaces';
import { constants } from 'utils';
import { Step as Assess } from '../Assess';
import { doesNotContainEmoji } from 'utils/validations';

type Values = Pick<Interfaces.Common.FormValues, 'identity'>;

// Refer: https://regex101.com/r/dkFASs/6
const RE_EMAIL =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const RE_NAME = /^([a-zA-Z]+((’ ?|' ?|-|—|–| )[a-zA-Z]+)*)\s*$/; // taken from react-loa2

const initialValues: Values = {
  identity: {
    confirmEmail: '',
    email: '',
    phone: '',
    familyName: '',
    givenName: '',
    whoIsTravelling: undefined,
    othersTravel: []
  }
};
const initialErrors: FormikErrors<Values> = {};
const initialTouched: FormikTouched<Values> = {};

const emailValidation = (text?: string) => !!text?.match(RE_EMAIL) && doesNotContainEmoji(text);

const wizardSchema = Yup.object().shape<Interfaces.Common.Identity>({
  email: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'isEmailValid',
      'That doesn’t look like a valid email. Please try again.',
      emailValidation
    )
    .max(80, constants.GenericValidationErrors.maxLength(80)),
  confirmEmail: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'isConfirmEmailValid',
      'That doesn’t look like a valid email. Please try again.',
      emailValidation
    )
    .test(
      'isEqual',
      'Sorry, that didn’t match. Please try again.',
      function validate(value: string) {
        const parent: Interfaces.Common.Identity = this.parent;
        return parent.email?.toLowerCase() === value?.toLowerCase();
      }
    ),
  familyName: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .matches(RE_NAME, 'That doesn’t look like a valid family name. Please try again.')
    .max(40, constants.GenericValidationErrors.maxLength(40)),
  givenName: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .matches(RE_NAME, 'That doesn’t look like a valid given name. Please try again.')
    .max(40, constants.GenericValidationErrors.maxLength(40)),
  phone: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test(
      'isValid',
      'That doesn’t look like a valid phone number. Please try again.',
      function validate(value?: string) {
        return !!value && isValidPhoneNumber(value);
      }
    ),
  whoIsTravelling: Yup.mixed()
    .required(constants.GenericValidationErrors.required)
    .oneOf(['Me', 'Me and my dependants or anyone under 12']),
  othersTravel: Yup.array().of(
    Yup.object().shape<Interfaces.Common.OtherTraveller>({
      id: Yup.mixed().notRequired(),
      name: Yup.string()
        .required(constants.GenericValidationErrors.required)
        .matches(RE_NAME, 'That doesn’t look like a valid name. Please try again.')
        .max(81, constants.GenericValidationErrors.maxLength(81))
    })
  )
});

const validationSchema = Yup.object().shape<Values>({
  identity: wizardSchema
}) as Yup.Schema<Values>;

const Component = React.lazy(() => import('./Identity'));

const Step: StepProps<Values> = {
  id: 'details',
  title: constants.Page.title,
  stepName: 'Your details',
  validation: {
    schema: validationSchema,
    fieldNames: (values: Values) => [
      'identity.email',
      'identity.confirmEmail',
      'identity.familyName',
      'identity.givenName',
      'identity.phone',
      'identity.whoIsTravelling',
      'identity.homeAddress',
      'identity.homeAddress.summaryData',
      'identity.homeAddress.fullDetails',
      'identity.homeAddress.fullDetails.fullAddress',
      'identity.homeAddress.fullDetails.postCode',
      'identity.homeAddress.fullDetails.state',
      'identity.homeAddress.fullDetails.suburb',
      ...values.identity.othersTravel.map((_, i) => `identity.othersTravel.${i}.name`)
    ]
  },
  Component,
  navigation: {
    normal: [
      {
        content: 'Next',
        shouldValidate: true,
        type: 'primary',
        onClick: (context, formProps) => {
          context.dispatch({
            type: 'GOTO_STEP',
            stepId: Assess.id,
            values: formProps.values
          });
        }
      }
    ]
  }
};

export { Step, initialErrors, wizardSchema, initialTouched, initialValues };
